/**
 * D4's own URL keys (spec §4.7, R18): active and all, on /audience only, in the URL and never remembered.
 * useDashboardFilters stays as it is; this hook sits beside it, as D3's useCreativeUrl does.
 *
 * - Whenever the URL does not carry the canonical form of the values in force (active=1 or any other value, all=0, a
 *   repeated key), ONE replace writes it, with the shared filters in force, as useScopeArrival does.
 *   useDashboardFilters may rewrite the URL in the same tick, and react-router's setter builds on the last render's
 *   URL, so whichever write lands last carries both. The check is by value, so nothing loops: the next render finds
 *   the URL carrying both and writes nothing.
 * - setAudience REPLACES the entry, as every dashboard filter does: Back leaves the page.
 *
 * @module hooks/useAudienceUrl
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  carriesAudienceUrl, parseAudienceUrl, writeAudienceUrl, writeShared, type AudienceUrlFilters, type SharedFilters,
} from '@/services/dashboardFilters';

interface AudienceUrl {
  audience: AudienceUrlFilters;
  setAudience: (patch: Partial<AudienceUrlFilters>) => void;
}

const REPLACE = { replace: true } as const;

export const useAudienceUrl = (shared: SharedFilters): AudienceUrl => {
  const [params, setParams] = useSearchParams();
  const search = params.toString();
  const audience = useMemo(() => parseAudienceUrl(new URLSearchParams(search)), [search]);
  const { activeOnly, showAll } = audience;
  const { period, objective, currency } = shared;

  useEffect(() => {
    const current = new URLSearchParams(search);
    const inForce = { activeOnly, showAll };
    if (!carriesAudienceUrl(current, inForce)) {
      setParams(writeAudienceUrl(writeShared(current, { period, objective, currency }), inForce), REPLACE);
    }
  }, [search, activeOnly, showAll, period, objective, currency, setParams]);

  const setAudience = useCallback((patch: Partial<AudienceUrlFilters>) => {
    setParams((prev) => writeAudienceUrl(prev, { ...parseAudienceUrl(prev), ...patch }), REPLACE);
  }, [setParams]);

  return useMemo(() => ({ audience, setAudience }), [audience, setAudience]);
};
